/**
 * Web settings page.
 *
 * Thin DI wrapper over the headless `SettingsPanel` from
 * `@reflecto/client-ui`. The headless component owns the section
 * layout + the two-step disconnect confirm flow + the loading/reset
 * state machine; this page wires the web-specific bindings:
 *
 *   - theme: webStorage + applyTheme
 *   - notifications: showOsNotifications + showCallNotifications via
 *     `useStorage` reads + `webStorage.set(...)` writes. Both default
 *     to ON: only the literal `false` disables; undefined (never
 *     written) is treated as on so the toggle reflects the actual
 *     handler default rather than a misleading "off" while storage
 *     hydrates.
 *   - DND snooze: `webStorage.set({ dndSnoozeUntil })` /
 *     `webStorage.remove("dndSnoozeUntil")`
 *   - privacy: `analyticsOptOut` via `useStorage` + `webStorage.set(...)`.
 *     The switch shows the PARTICIPATING state (inverse of the stored
 *     opt-out flag). Toggle order is load-bearing — see the handler's
 *     own comment (docs/analytics/event-spec.md §0 rule 7 + §4.1).
 *   - connection: paired (from `useStorage("paired")`), connected
 *     (from `useStorage("sseConnected")`), onDisconnect bridging the
 *     `disconnect()` service into a Promise contract
 *   - About: version from the build-time `__APP_VERSION__` define
 *
 * Partial-success handling: the disconnect service can return
 * `success: true, localCleanupOk: false` when the server unpair lands
 * but the IDB clear / push unsubscribe blows up afterwards. The
 * headless `onDisconnect` contract is `Promise<void>` (resolve on
 * success, reject on retry-able failure) — not rich enough to encode
 * the "disconnected but please reload" middle state. We surface that
 * branch via a top-of-page error banner (kept outside the headless
 * component) and resolve the Promise so the headless's reset path
 * still runs cleanly.
 */

import { LOCALE_ENDONYMS } from "@reflecto/client-core";
import {
  Box,
  DndEditor,
  type DndEditorLabels,
  SettingsPanel,
  type SettingsPanelExtraSection,
  type SettingsPanelLabels,
  type SettingsPanelPrivacyLabels,
  Text,
  type ThemeValue,
} from "@reflecto/client-ui";
import { useCallback, useMemo, useState } from "react";
import { webStorage } from "../adapters/storage.web";
import { Connectors } from "../components/Connectors";
import { useStorage } from "../hooks/useStorage";
import { catalogs, LANGUAGE_OVERRIDE_KEY, messages, readLanguageOverride } from "../i18n";
import { logWarn } from "../lib/log";
import { navigate } from "../lib/router";
import { applyTheme } from "../lib/theme";
import { getAnalytics } from "../services/analytics";
import { disconnect } from "../services/disconnect";

/**
 * Picker options: every registered catalog except `en`'s fallback slot is
 * still listed — en is a selectable language like any other; "System
 * default" (null) is the follow-the-browser choice. Endonym-labeled;
 * locales missing from LOCALE_ENDONYMS are hidden rather than shown as
 * raw tags.
 */
const LANGUAGE_OPTIONS = Object.keys(catalogs)
  .filter((tag) => tag in LOCALE_ENDONYMS)
  .sort((a, b) => LOCALE_ENDONYMS[a].localeCompare(LOCALE_ENDONYMS[b]))
  .map((tag) => ({ tag, endonym: LOCALE_ENDONYMS[tag] }));

/**
 * Persist the language choice and reload. Two writes by design: the
 * localStorage copy is what `detectLocale()` reads synchronously at the
 * next module load; the ReflectoStorage mirror is what the service worker
 * reads to localize its toasts (it has no localStorage). The reload is the
 * switch mechanism — the catalog is resolved once per page load.
 */
async function handleLanguageChange(tag: string | null): Promise<void> {
  try {
    if (tag === null) {
      localStorage.removeItem(LANGUAGE_OVERRIDE_KEY);
    } else {
      localStorage.setItem(LANGUAGE_OVERRIDE_KEY, tag);
    }
  } catch {
    // Storage blocked (private mode edge cases) — the mirror write below
    // still lands; the page keeps the browser language until it works.
  }
  if (tag === null) {
    await webStorage.remove("languageOverride");
  } else {
    await webStorage.set({ languageOverride: tag });
  }
  location.reload();
}

const LABELS: SettingsPanelLabels = {
  back: messages.settings.back,
  settings: messages.settings.settings,
  appearance: messages.settings.appearance,
  theme: messages.settings.theme,
  themeAuto: messages.settings.themeAuto,
  themeLight: messages.settings.themeLight,
  themeDark: messages.settings.themeDark,
  notificationsSection: messages.settings.notificationsSection,
  osNotifications: messages.settings.osNotifications,
  callNotifications: messages.settings.callNotifications,
  connection: messages.settings.connection,
  status: messages.settings.status,
  statusConnected: messages.settings.statusConnected,
  statusReconnecting: messages.settings.statusReconnecting,
  statusDisconnected: messages.settings.statusDisconnected,
  disconnectFromPhone: messages.settings.disconnectFromPhone,
  disconnectConfirm: messages.settings.disconnectConfirm,
  yesDisconnect: messages.settings.yesDisconnect,
  cancel: messages.settings.cancel,
  aboutAndSupport: messages.settings.aboutAndSupport,
  version: messages.settings.version,
  encryption: messages.settings.encryption,
  encryptionValue: messages.settings.encryptionValue,
  needHelp: messages.settings.needHelp,
  sendFeedback: messages.feedback.sendFeedback,
};

const PRIVACY_LABELS: SettingsPanelPrivacyLabels = {
  section: messages.settings.privacySection,
  analyticsToggle: messages.settings.analyticsToggle,
  analyticsToggleDescription: messages.settings.analyticsToggleDescription,
};

const DND_LABELS: DndEditorLabels = {
  title: messages.dnd.title,
  description: messages.dnd.description,
  snooze1h: messages.dnd.snooze1h,
  snoozeActiveUntil: messages.dnd.snoozeActiveUntil,
  cancelSnooze: messages.dnd.cancelSnooze,
};

export function SettingsPage({ onSendFeedback }: { onSendFeedback?: () => void }) {
  const { value: themeStored } = useStorage("theme");
  const theme = themeStored ?? "auto";
  const { value: paired } = useStorage("paired");
  const { value: sseConnected } = useStorage("sseConnected");
  const { value: showOsStored } = useStorage("showOsNotifications");
  const { value: showCallStored } = useStorage("showCallNotifications");
  const { value: dndSnoozeUntilStored } = useStorage("dndSnoozeUntil");
  // Match the SW's defaulting rule: `undefined` (never written) and
  // `true` both display as on; only the literal `false` displays as
  // off. Mirrors the extension's `useStorage("showOsNotifications") ?? true`.
  const showOsNotifications = showOsStored !== false;
  const showCallNotifications = showCallStored !== false;
  const { value: analyticsOptOutStored } = useStorage("analyticsOptOut");
  // `analyticsOptOut` is undefined by default (participating) — the switch
  // shows the PARTICIPATING state, i.e. the inverse of the stored flag.
  const analyticsEnabled = analyticsOptOutStored !== true;
  const [error, setError] = useState<string | null>(null);

  // Explicit `ThemeValue` rather than `typeof theme` — the latter
  // is opaque about intent (a reader has to chase up to the
  // `useStorage("theme")` call to see the union) and would silently
  // widen if `useStorage`'s return type ever changes.
  const handleThemeChange = useCallback((value: ThemeValue) => {
    // `applyTheme` is synchronous and lands the visual change
    // immediately; the IDB write is best-effort. If the write
    // fails (storage quota, IDB closed mid-shutdown) we'd silently
    // revert to the prior theme on next reload — surface via the
    // structured log so the failure is at least debuggable rather
    // than completely invisible. UI state stays correct for the
    // current session either way.
    webStorage.set({ theme: value }).catch((err) => logWarn("ui", "theme_write_failed", err));
    applyTheme(value);
  }, []);

  // Notification toggle persistence — same `.catch + logWarn` shape
  // as the theme toggle so a failed IDB write doesn't silently leave
  // the user's preference unset. The headless `SettingsPanel` updates
  // the visible switch state from props, so a missed write reverts
  // on the next render after the (eventual) `useStorage` re-fetch.
  const handleOsNotificationsChange = useCallback((value: boolean) => {
    webStorage
      .set({ showOsNotifications: value })
      .catch((err) => logWarn("ui", "show_os_notifications_write_failed", err));
  }, []);

  const handleCallNotificationsChange = useCallback((value: boolean) => {
    webStorage
      .set({ showCallNotifications: value })
      .catch((err) => logWarn("ui", "show_call_notifications_write_failed", err));
  }, []);

  // Ordering is load-bearing (event-spec §4.1): capture the opt-out event BEFORE
  // halting; on opt-in, resume BEFORE capturing. Never awaited.
  const handleAnalyticsToggle = useCallback((enabled: boolean) => {
    if (!enabled) {
      getAnalytics().capture("analytics_opt_out_toggled", { enabled: false });
      getAnalytics().setOptedOut(true);
      webStorage
        .set({ analyticsOptOut: true })
        .catch((err) => logWarn("ui", "analytics_opt_out_write_failed", err));
    } else {
      webStorage
        .set({ analyticsOptOut: false })
        .catch((err) => logWarn("ui", "analytics_opt_out_write_failed", err));
      getAnalytics().setOptedOut(false);
      getAnalytics().capture("analytics_opt_out_toggled", { enabled: true });
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    setError(null);
    const result = await disconnect();
    if (!result.success) {
      // The server unpair itself failed — the device is still
      // paired server-side and the user can retry without losing
      // local auth material. Throw so the headless component resets
      // its confirm dialog.
      const msg = result.error ?? messages.settings.disconnectFailed;
      setError(msg);
      throw new Error(msg);
    }
    if (!result.localCleanupOk) {
      // Server unlinked us, but the IDB clear / push unsubscribe
      // threw afterwards. The token is now invalid, so a retry
      // would just bounce off the auth gate. Surface the "please
      // reload" hint and let the headless reset to the primary
      // button (resolve the Promise) — there's nothing to retry.
      setError(
        result.error
          ? messages.settings.disconnectLocalCleanupFailedWithError(result.error)
          : messages.settings.disconnectLocalCleanupFailed
      );
      return;
    }
    // Fully clean: clear hash + let App's `paired` listener route
    // back to PairingPage on its own. (We can't `navigate({
    // name: "main" })` here because `App.tsx` reads `paired` first;
    // by the time it re-renders the storage-clear has already
    // landed, so the unpaired branch wins regardless.)
    navigate({ name: "main" });
  }, []);

  const handleSnoozeFor = useCallback((durationMs: number) => {
    webStorage
      .set({ dndSnoozeUntil: Date.now() + durationMs })
      .catch((err) => logWarn("ui", "dnd_snooze_write_failed", err));
  }, []);

  const handleCancelSnooze = useCallback(() => {
    webStorage
      .remove("dndSnoozeUntil")
      .catch((err) => logWarn("ui", "dnd_snooze_remove_failed", err));
  }, []);

  // DND + Connectors are paired-only (DND's snooze needs a partner to gate;
  // connectors are owned by the paired device's JWT).
  const extraSections: SettingsPanelExtraSection[] = useMemo(() => {
    const sections: SettingsPanelExtraSection[] = [];
    if (paired) {
      sections.push({
        id: "dnd",
        title: messages.dnd.title,
        content: (
          <DndEditor
            snoozeUntil={dndSnoozeUntilStored}
            onSnoozeFor={handleSnoozeFor}
            onCancelSnooze={handleCancelSnooze}
            labels={DND_LABELS}
          />
        ),
      });
      sections.push({
        id: "connectors",
        title: messages.connectors.title,
        content: <Connectors />,
      });
    }
    return sections;
  }, [paired, dndSnoozeUntilStored, handleSnoozeFor, handleCancelSnooze]);

  return (
    // Two flex:1+min-height:0 boxes: outer fills #root, inner gives the panel a definite height below the banner.
    <Box direction="column" style={{ flex: 1, minHeight: 0 }}>
      {error && (
        <Box paddingX="lg" paddingY="md">
          <Text size="s" tone="negative">
            {error}
          </Text>
        </Box>
      )}
      <Box direction="column" style={{ flex: 1, minHeight: 0 }}>
        <SettingsPanel
          theme={theme}
          onThemeChange={handleThemeChange}
          language={{
            value: readLanguageOverride(),
            options: LANGUAGE_OPTIONS,
            onChange: (tag) => {
              void handleLanguageChange(tag);
            },
            labels: {
              language: messages.settings.language,
              systemDefault: messages.settings.languageSystemDefault,
            },
          }}
          osNotifications={{
            value: showOsNotifications,
            onChange: handleOsNotificationsChange,
          }}
          callNotifications={{
            value: showCallNotifications,
            onChange: handleCallNotificationsChange,
          }}
          privacy={{
            analyticsEnabled: {
              value: analyticsEnabled,
              onChange: handleAnalyticsToggle,
            },
            labels: PRIVACY_LABELS,
          }}
          connection={{
            paired: paired ?? false,
            connected: sseConnected ?? false,
            onDisconnect: handleDisconnect,
          }}
          extraSections={extraSections}
          onSendFeedback={onSendFeedback}
          version={__APP_VERSION__}
          supportEmail="support@reflecto.dev"
          onBack={() => navigate({ name: "main" })}
          labels={LABELS}
        />
      </Box>
    </Box>
  );
}
